import React, {useState} from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {toast} from "react-toastify";
import _ from "lodash";
import {faIcons} from "../../../utils/iconConstants";
import {toastSuccess, toastWarning} from "../../../utils/toast";
import SelectDropdown from "../../Components/Dropdowns/SelectDropdown";
import {storeCellIds, universeLabel} from "./storeUniverseTableHelpers";
import {
    CELL_OVERRIDE_FIELDS,
    CELL_OVERRIDE_TYPES,
    overrideValues,
    saveCanonicalStoreOverride,
} from "../canonicalStoreOverrides";

const cellTypeNoun = (cellType) => cellType.toLowerCase();

const toCellOverrideMaps = (values) => Object.fromEntries(CELL_OVERRIDE_FIELDS.map(field => [
    field,
    Object.fromEntries(values[field].map(({universe, cellId}) => [universe, cellId])),
]));

const toCellOverrideEntries = (cellOverrides) => Object.fromEntries(CELL_OVERRIDE_FIELDS.map(field => [
    field,
    Object.keys(cellOverrides[field]).sort().map(universe => ({universe, cellId: cellOverrides[field][universe]})),
]));

const cellOptionLabel = ({cellId, cellDescription, geographicRegion}) => {
    const name = cellDescription || geographicRegion;
    return `${cellId}${name ? ` – ${name}` : ''}`;
};

const cellOptions = (projectionCells, universe, cellType, selected) => {
    const options = projectionCells
        .filter(cell => cell.universe === universe && cell.cellType === cellType)
        .map(cell => ({label: cellOptionLabel(cell), value: cell.cellId}));
    return selected === undefined || options.some(({value}) => value === selected)
        ? options
        : [...options, {label: `${selected}`, value: selected}];
};

const CellOverrideModal = ({version, store, override, projectionCells, projectionCellsFailed, onClose, onSaved}) => {
    const universes = store.universe ?? [];
    const [initialCellOverrides] = useState(() => toCellOverrideMaps(overrideValues(override)));
    const [cellOverrides, setCellOverrides] = useState(initialCellOverrides);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const canSubmit = !saving && !!projectionCells && !_.isEqual(cellOverrides, initialCellOverrides);

    const removeCellOverride = (field, universe) =>
        setCellOverrides(previous => {
            const {[universe]: _removed, ...kept} = previous[field];
            return {...previous, [field]: kept};
        });

    const setCellOverride = (field, universe, selection) => selection
        ? setCellOverrides(previous => ({...previous, [field]: {...previous[field], [universe]: selection.value}}))
        : removeCellOverride(field, universe);

    const inactiveOverrides = CELL_OVERRIDE_TYPES.flatMap(({field, cellType}) =>
        Object.keys(cellOverrides[field])
            .filter(universe => !universes.includes(universe))
            .sort()
            .map(universe => ({field, noun: cellTypeNoun(cellType), universe, cellId: cellOverrides[field][universe]})));

    const onSubmit = () => {
        setSaving(true);
        setSaveError(null);
        return saveCanonicalStoreOverride(version, store.canonicalStoreId, {
            ...overrideValues(override),
            ...toCellOverrideEntries(cellOverrides),
        })
            .then(response => {
                toastSuccess(toast, `Saved the cell overrides for ${store.canonicalStoreId}`);
                response.warnings.forEach(warning => toastWarning(toast, warning.message, {autoClose: 3000}));
                onSaved(response);
            })
            .catch(error => {
                setSaveError(error.message);
                setSaving(false);
            });
    };

    const cellField = (universe, {field, cellType}) => {
        const noun = cellTypeNoun(cellType);
        const inputId = `store-cell-override-${universe}-${cellType}`;
        const currentCellIds = storeCellIds(store.cells, universe, cellType);
        const selected = cellOverrides[field][universe];
        const options = cellOptions(projectionCells, universe, cellType, selected);
        return (
            <div className={"store-info-field store-override-field"} key={field}>
                <label className={"store-info-field-label"} htmlFor={inputId}>{`${_.capitalize(noun)} cell`}</label>
                {options.length
                    ? <SelectDropdown
                        id={`${inputId}_dropdown`}
                        inputId={inputId}
                        containerClassName={"store-override-cells"}
                        dataTestId={`${inputId}-dropdown`}
                        isClearable
                        placeholder={'No Override'}
                        options={options}
                        selectValue={selected ?? null}
                        selectLabel={options.find(({value}) => value === selected)?.label}
                        isDisabled={saving}
                        menuPortalTarget={document.body}
                        styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                        onChange={selection => setCellOverride(field, universe, selection)}
                    />
                    : <div className={"store-override-current"} data-testid={`${inputId}-none`}>No {noun} cells in this universe</div>}
                <div className={"store-override-source-values"}>
                    <div className={"store-override-current"} data-testid={`${inputId}-current`}>
                        Current: {currentCellIds.join(', ') || 'None'}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            isOpen={true}
            className="Modal store-override-modal store-cell-override-modal"
            overlayClassName="Overlay"
            ariaHideApp={false}
            shouldCloseOnOverlayClick={false}
            onRequestClose={() => !saving && onClose()}
            testId={"store-cell-override-modal"}
        >
            <h5 className={"modal-header-text"}>Override Canonical Store Cells</h5>
            <div className={"store-override-store"}>
                <div className={"store-info-field"}>
                    <div className={"store-info-field-label"}>Canonical Store</div>
                    <div className={"store-info-field-value"} data-testid={"store-cell-override-canonical-store-id"}>{store.canonicalStoreId}</div>
                </div>
                <div className={"store-info-field"}>
                    <div className={"store-info-field-label"}>Store</div>
                    <div className={"store-info-field-value"} data-testid={"store-cell-override-store-name"}>{store.storeName || store.bannerName}</div>
                </div>
            </div>
            <div className={"callout primary small store-override-help"}>
                <i className={faIcons.infoCircle} aria-hidden={true}/>
                The cell picked for a universe replaces all of the store's cells of that type in it. Clear it to use the cells from the universe file.
            </div>
            {projectionCellsFailed &&
                <div className={'error'} role={'alert'} data-testid={"store-cell-override-load-error"}>The cells could not be loaded</div>}
            {!projectionCells && !projectionCellsFailed &&
                <div data-testid={"store-cell-override-loading"}><i className={`${faIcons.spinner} fa-spin`}/> Loading cells</div>}
            {projectionCells && !universes.length &&
                <p data-testid={"store-cell-override-no-universes"}>
                    The store is not in any universe. Add it to a universe with Store Overrides first.
                </p>}
            {projectionCells && universes.map(universe => (
                <div className={"store-cell-override-universe"} key={universe} data-testid={`store-cell-override-${universe}`}>
                    <h3>{universeLabel(universe)}</h3>
                    <div className={"store-override-fields"}>
                        {CELL_OVERRIDE_TYPES.map(cellOverrideType => cellField(universe, cellOverrideType))}
                    </div>
                </div>
            ))}
            {inactiveOverrides.length > 0 &&
                <div className={"store-cell-override-inactive"} data-testid={"store-cell-override-inactive"}>
                    <div className={"store-info-field-label"}>Inactive overrides</div>
                    {inactiveOverrides.map(({field, noun, universe, cellId}) => (
                        <div className={"store-cell-override-inactive-entry"} key={`${field}-${universe}`}>
                            <span>{universeLabel(universe)} · {noun} {cellId} (store is not in this universe)</span>
                            <button type={"button"} className={"store-cell-override-remove"} disabled={saving}
                                    onClick={() => removeCellOverride(field, universe)}>
                                Remove
                            </button>
                        </div>
                    ))}
                </div>}
            {saveError && <div className={'error'} role={'alert'} data-testid={"store-cell-override-save-error"}>{saveError}</div>}
            <div className={"right-things"}>
                <button type="button" className="button modal-button cancel" name="cancel"
                        onClick={onClose} disabled={saving}>
                    Cancel
                </button>
                <button type="button" className={"button modal-button confirm"} name="submit"
                        onClick={onSubmit} disabled={!canSubmit}>
                    {saving
                        ? <><i className={`${faIcons.spinner} fa-spin`} data-testid={"spinner"}/><span>Saving</span></>
                        : "Submit"}
                </button>
            </div>
        </Modal>
    );
};

const cellOverridePropType = PropTypes.arrayOf(PropTypes.shape({
    universe: PropTypes.string,
    cellId: PropTypes.number,
}));

CellOverrideModal.propTypes = {
    version: PropTypes.string.isRequired,
    store: PropTypes.shape({
        canonicalStoreId: PropTypes.string.isRequired,
        storeName: PropTypes.string,
        bannerName: PropTypes.string,
        universe: PropTypes.arrayOf(PropTypes.string),
        cells: PropTypes.arrayOf(PropTypes.shape({
            cellId: PropTypes.number,
            name: PropTypes.string,
            universe: PropTypes.string,
        })),
    }).isRequired,
    override: PropTypes.shape({
        overrideStandardCells: cellOverridePropType,
        overrideCensusCells: cellOverridePropType,
    }),
    projectionCells: PropTypes.arrayOf(PropTypes.shape({
        cellId: PropTypes.number,
        cellType: PropTypes.string,
        universe: PropTypes.string,
        cellDescription: PropTypes.string,
        geographicRegion: PropTypes.string,
    })),
    projectionCellsFailed: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
};

export default CellOverrideModal;
